// imports
import { forEach } from 'lodash';
import './styles/graphic.scss';
import * as d3 from 'd3';

import tippy from 'tippy.js';
import 'tippy.js/dist/tippy.css'; // optional styling


let svg;
let xAxis;
let yAxis;
let zeroLine;
let moreAffordable;
let lessAffordable;
let affordableLabels;
let affordabilityLabel;
let xAxisLabel;
let margin;
let width;
let height;
let shadingRect;
let chartGroup;
let prevValProp;
let prevWidth;
let prevSelectedPlaces = new Set([]);

let xScale;
let yScale;

const defaultTransitionTime = 300;
const heightTotal = 500;
const DOT_RADIUS = 4;
const X_MAX = 200;

let jsonDataGlobal;
let simulation;

function getMargins() {
  return window.innerWidth < 600
    ? {
      top: 24, right: 15, bottom: 40, left: 15,
    }
    : {
      top: 24, right: 15, bottom: 40, left: 15,
    };
}

function setDimensions(container) {
  margin = getMargins();
  width = container.node().getBoundingClientRect().width - margin.left - margin.right;
  height = heightTotal - margin.top - margin.bottom;
}

function ticked(x100, rScale) {
  const rPad = 0.5;

  chartGroup.selectAll('.dot')
    .attr('cx', (d) => {
      const r = (rScale(d.pop) + rPad) * 0.75;
      if (d.score < 100) {
        d.x = Math.min(d.x, x100 - r);
      } else {
        d.x = Math.max(d.x, x100 + r);
      }
      return d.x;
    })
    .attr('cy', d => d.y);
}


// get the url parameters
let queryString = window.location.search;

try {
  const parentHref = window.parent.location.href;

  // Check if we're inside a parent.html
  if (window.parent !== window && parentHref.includes('parent.html')) {
    queryString = window.parent.location.search || queryString;
  }
} catch (err) {
  // Cross-origin error, stick with current location
  console.warn('Could not access parent window:', err);
}

const urlParams = new URLSearchParams(queryString);

// create variables with them all, or their defaults
const c1 = urlParams.has('c1') ? urlParams.get('c1') : '3E7D70';
const c2 = urlParams.has('c2') ? urlParams.get('c2') : '7FC4B7';
const d1 = urlParams.has('d1') ? +urlParams.get('d1') : 60;
const d2 = urlParams.has('d2') ? +urlParams.get('d2') : 110;


const colorScale = d3.scaleLinear()
  .domain([
    d1, // 60
    d1 + (d2 - d1) * 0.2, // 60 + 10 = 70
    d1 + (d2 - d1) * 0.4, // 60 + 20 = 80
    d1 + (d2 - d1) * 0.6, // 60 + 30 = 90
    d1 + (d2 - d1) * 0.8, // 60 + 40 = 100 (threshold)
    d1 + (d2 - d1) * 0.9, // 60 + 45 = 105
    d2, // 110
  ])
  .range(['#F18851', '#F18851', '#F18851', '#F1C40F', '#F1C40F', '#53A796', '#53A796']) // Gradient: unaffordable -> yellow -> affordable
  // .domain([d1, d2])
  // .range([`#${c1}`, `#${c2}`])
  .interpolate(d3.interpolateRgb)
  .clamp(true);

// const colorScale = d3.scaleDiverging()
//   .domain([d1, d1 + (d2 - d1) * 0.5, d2])
//   .interpolator(t => d3.interpolatePuOr(1 - t))
//   .clamp(true);

// .range(['#f18851', '#53A796']) 2 color
// .range(['#3E7D70', '#7FC4B7']) greens

function setupChart(container) {
  setDimensions(container);
  svg = container.append('svg')
    .attr('width', width + margin.left + margin.right)
    .attr('height', height + margin.top + margin.bottom);


  chartGroup = svg.append('g')
    .attr('transform', `translate(${margin.left},${margin.top})`);

  shadingRect = chartGroup.append('rect')
    .attr('class', 'shading')
    .attr('width', 0)
    .attr('height', 0)
    .attr('y', 0)
    .attr('fill', '#f0f0f0')
    .lower(); // Ensure this is at the bottom

  xAxis = chartGroup.append('g')
    .attr('class', 'x-axis')
    .attr('transform', `translate(0,${height})`);

  // yAxis = chartGroup.append('g')
  //   .attr('transform', `translate(${width / 2},0)`)
  //   .attr('class', 'y-axis');

  zeroLine = chartGroup.append('line')
    .attr('class', 'zero-line')
    .attr('y1', -6)
    .attr('y2', height + 6)
    .lower();

  affordableLabels = chartGroup.append('g');

  moreAffordable = affordableLabels
    .append('g')
    .attr('transform', 'translate(10,0)');


  const moreAffordableTxt = moreAffordable
    .append('text')
    .attr('class', 'more-affordable')
    .attr('text-anchor', 'start')
    .attr('dx', '0')
    .attr('dy', '0')
    .text('More affordable');

  const moreAffordableArrow = moreAffordable.append('g')
    .attr('transform', 'translate(112,-12) rotate(90) scale(.8)')
    .attr('class', 'arrow')
    .append('polygon')
    .attr('points', '19.68 9.84 9.84 0 0 9.84 4.44 9.84 4.44 23.39 15.24 23.39 15.24 9.84 19.68 9.84')
    .attr('fill', 'var(--color-affordable)')
    .attr('fill-opacity', '1');

  lessAffordable = affordableLabels
    .append('g')
    .attr('transform', 'translate(-10,0)');

  const lessAffordableTxt = lessAffordable
    .append('text')
    .attr('class', 'less-affordable')
    .attr('text-anchor', 'end')
    .attr('dx', '0')
    .attr('dy', '0')
    .text('More expensive');

  const lessAffordableArrow = lessAffordable.append('g')
    .attr('transform', 'translate(-112,4) rotate(-90) scale(.8)')
    .attr('class', 'arrow')
    .append('polygon')
    .attr('points', '19.68 9.84 9.84 0 0 9.84 4.44 9.84 4.44 23.39 15.24 23.39 15.24 9.84 19.68 9.84')
    .attr('fill', 'var(--color-unaffordable)')
    .attr('fill-opacity', '1');

  affordabilityLabel = chartGroup
    .append('text')
    .attr('class', 'affordability-label')
    .attr('text-anchor', 'middle') // center alignment
    .attr('opacity', '0');

  affordabilityLabel.append('tspan')
    .attr('x', 0)
    .attr('dy', 0)
    .attr('text-anchor', 'middle')
    .text('Affordability threshold');

  // Axis label under x-axis
  xAxisLabel = chartGroup.append('text')
    .attr('class', 'axis-label')
    .attr('transform', `translate(${width / 2},${height + 34})`)
    .attr('text-anchor', 'middle')
    .text('Affordability Index');
}

function updateChart(val_prop) {
  const newData = jsonDataGlobal.data.metros.map(d => ({
    score: +d[val_prop],
    region: d.region,
    label: d.name,
    display: `${d.place}, ${d.state} area`,
    pop: d.pop,
  })).filter(d => d.score != null && +d.score > 0);

  // forEach(newData, (d) => {
  //   if (!d.region) console.log(d);
  // });

  const isCurrent = val_prop === 'last_score';
  svg.classed('current', isCurrent);
  svg.classed('historic', !isCurrent);

  const popExtent = d3.extent(newData, d => d.pop);
  const radiusScale = d3.scaleSqrt()
    .domain(popExtent)
    .range([2, 10]); // min and max bubble radius you want

  const x = d3.scaleLinear()
    .domain([0, X_MAX])
    .nice()
    .range([0, width]);

  const y = d3.scaleBand()
    .domain(['West', 'Midwest', 'South', 'Northeast'])
    .range([0, height])
    .padding(0.5);

  // Update the x-axis with only 0, 100, 200 ticks
  xAxis.transition().duration(defaultTransitionTime)
    .call(d3.axisBottom(x)
      .tickValues([0, 100, 200])
      .tickFormat(d3.format('d'))
      .tickSizeOuter(0));

  // Align first and last tick labels
  const ticks = xAxis.selectAll('.tick');
  // default center
  ticks.select('text').style('text-anchor', 'middle').attr('dx', null);
  // first (0) -> left align
  // ticks.filter(d => d === 0).select('text')
  //   .style('text-anchor', 'start')
  //   .attr('dx', '0.5em');
  // // last (200) -> right align
  // ticks.filter(d => d === 200).select('text')
  //   .style('text-anchor', 'end')
  //   .attr('dx', '-0.5em');

  // Map existing nodes by label for position persistence
  const oldNodes = simulation ? simulation.nodes() : [];
  const oldNodesMap = new Map(oldNodes.map(d => [d.label, d]));

  // For each new data point, set targets and preserve old position if exists
  newData.forEach((d) => {
    d.xTarget = x(d.score);
    d.yTarget = y(d.region) + y.bandwidth() / 2;

    const old = oldNodesMap.get(d.label);
    if (old) {
      d.x = old.x; // preserve current position
      d.y = old.y;
      d.vx = old.vx; // preserve velocity to smooth motion
      d.vy = old.vy;
    } else {
      d.x = d.xTarget; // start new nodes at target (optional)
      d.y = d.yTarget;
    }
  });

  shadingRect
    .attr('width', x(100))
    .attr('height', height);

  zeroLine
    .attr('x1', x(100))
    .attr('x2', x(100));

  affordableLabels
    .attr('transform', `translate(${x(100)}, ${14})`);

  affordabilityLabel
  // .attr('transform', `translate(${x(100)}, ${height + 20})`)
    .attr('transform', `translate(${x(100)}, -10)`)
    .attr('opacity', '1');

  const dots = chartGroup.selectAll('.dot')
    .data(newData, d => d.label);

  dots.exit()
    .transition().duration(defaultTransitionTime)
    .attr('r', 0)
    .remove();

  const newDots = dots.enter().append('circle')
    .attr('class', 'dot metro')
    .attr('data-name', d => d.label)
    .attr('data-display', d => d.display)
    .attr('r', 0);

  newDots.each(function (d) {
    tippy(this, {
      onShow(instance) {
        const el = instance.reference;
        const name = el.getAttribute('data-display');
        const score = el.getAttribute('data-score');
        instance.setContent(`<strong>${name}</strong><br>Index: ${score}`);
      },
      allowHTML: true,
      placement: 'top',
      arrow: true,
      theme: 'light',
      delay: [100, 0],
    });
  });

  const allDots = chartGroup.selectAll('.dot')
    .data(newData, d => d.label)
    .classed('selected', d => prevSelectedPlaces && prevSelectedPlaces.has(d.label));

  allDots.select('title')
    .text(d => `${d.label}\nScore: ${d.score}`);

  allDots
    .attr('data-score', d => d.score.toFixed(1))
    .on('mouseenter', function () { d3.select(this).classed('highlight', true); })
    .on('mouseleave', function () { d3.select(this).classed('highlight', false); });

  allDots.transition().duration(defaultTransitionTime)
    .attr('r', d => radiusScale(d.pop))
    .attr('fill', d => colorScale(+d.score));

  if (!simulation) {
    simulation = d3.forceSimulation(newData)
      .force('x', d3.forceX(d => d.xTarget).strength(0.5))
      .force('y', d3.forceY(d => d.yTarget).strength(0.3))
      .force('collide', d3.forceCollide(d => radiusScale(d.pop) + 1))
      .alphaDecay(0.1)
      .on('tick', ticked); // ⬅️ use named tick function
  } else {
    simulation.nodes(newData);
    simulation.force('x').initialize(newData);
    simulation.force('y').initialize(newData);
    simulation.alpha(0.7).restart();
  }

  const x100 = x(100);

  if (!simulation) {
    simulation = d3.forceSimulation(newData)
      .force('x', d3.forceX(d => d.xTarget).strength(0.5))
      .force('y', d3.forceY(d => d.yTarget).strength(0.3))
      .force('collide', d3.forceCollide(d => radiusScale(d.pop) + 1))
      .alphaDecay(0.1)
      .on('tick', () => ticked(x100, radiusScale));
  } else {
    simulation.nodes(newData);
    simulation.force('x').initialize(newData);
    simulation.force('y').initialize(newData);
    simulation.alpha(0.7).restart();
    simulation.on('tick', () => ticked(x100, radiusScale)); // reapply with updated x(100)
  }

  svg.selectAll('.region-label')
    .data(y.domain())
    .enter()
    .append('text')
    .attr('class', 'region-label')
    .attr('x', margin.left + 4) // adjust to left-align or center
    .attr('y', d => y(d) + (y.bandwidth()))
    .attr('dy', d => (window.innerWidth < 700 ? '-16' : '0'))
    .text(d => d)
    .attr('font-weight', '500')
    .attr('text-anchor', 'start'); // or 'middle' if centering
}


export default function initDodgePlot(config, container, isMobile, jsonData, val_prop = 'last_score', selectedPlaces = new Set([])) {
  const initialWidth = config && config.initialWidth || null;
  const shouldRedraw = initialWidth && initialWidth !== prevWidth;
  const shouldUpdate = val_prop && val_prop !== prevValProp;

  jsonDataGlobal = jsonData;

  prevSelectedPlaces = selectedPlaces;

  if (!svg || shouldRedraw) {
    container.selectAll('svg').remove();
    svg = null;
    setupChart(container);
    updateChart(val_prop);
  } else if (shouldUpdate) {
    updateChart(val_prop);
  } else {
    // just selected places changing
    const dots = [...document.querySelectorAll('.dot.metro')];
    dots.forEach((dot) => {
      const name = dot.getAttribute('data-name');
      if (selectedPlaces.has(name)) {
        dot.classList.add('selected');
      } else {
        dot.classList.remove('selected');
      }
    });
  }

  [...document.querySelectorAll('.graphic')].forEach(
    (d) => {
      if (selectedPlaces && selectedPlaces.size > 0) {
        d.classList.add('solo');
      } else {
        d.classList.remove('solo');
      }
    },
  );

  if (config) prevWidth = initialWidth;
  if (val_prop) prevValProp = val_prop;
  if (window.pymChild) window.pymChild.sendHeight();
}
